import React from 'react';
import { Shield, Lock, Server, Globe, Mail } from 'lucide-react';

const GrammarFixPrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-8">
            <img
              src="/images/gClouds-logo.png"
              alt="gClouds"
              className="h-16 md:h-20"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Privacy Policy for "Grammar Fixer (Gemini)" Extension
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
            Your key, your data.
          </p>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Introduction */}
          <section className="bg-slate-900/50 backdrop-blur-xl p-8 rounded-xl border border-slate-800">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-400" />
              Core Principle: Your Data is Yours
            </h2>
            <p className="text-slate-300 mb-4">
              This extension, "Grammar Fixer (Gemini)" (the "Extension"), is built on the principle of "Your key, your data." It is designed to function without the developers ever seeing, storing, or having access to your personal information, your writing, or your API key.
            </p>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <p className="text-blue-400">Effective Date: September 16, 2025</p>
            </div>
          </section>

          {/* Data Collection */}
          <section className="bg-slate-900/50 backdrop-blur-xl p-8 rounded-xl border border-slate-800">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-blue-400" />
              Information Handling Specific to This Extension
            </h2>
            <div className="space-y-4">
                <p className="text-slate-300 mb-4">
                    The Extension's functionality requires handling two specific pieces of data:
                </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>
                    <strong>Text for Correction:</strong> To perform its function, the Extension captures the text you select or the content of the text field you are in. This text is sent directly from your browser to the Google Generative Language API for the sole purpose of grammar and spelling correction. This data is processed by Google according to their privacy policy and is never transmitted to, stored by, or viewed by the developers of this Extension.
                </li>
                <li>
                    <strong>Google AI Studio API Key:</strong> The Extension requires your personal API key to authenticate with Google's service. As seen in popup window, this key is saved using the <code>chrome.storage.sync</code> API. This means it is
  stored securely by your Chrome browser and synced with your Google Account. The developers have no access to this key. It is only
   used by the Extension in your browser to make requests to Google's API.
                </li>
              </ul>
            </div>
          </section>

          {/* Third-Party Service */}
          <section className="bg-slate-900/50 backdrop-blur-xl p-8 rounded-xl border border-slate-800">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="w-6 h-6 text-blue-400" />
              Third-Party Service: Google Generative Language API
            </h2>
            <p className="text-slate-300 mb-4">
              As declared in the <code>manifest.json</code> file's <code>host_permissions</code>, this Extension's only external
  communication is with the Google Generative Language API (<code>https://generativelanguage.googleapis.com/*</code>). Your use of
  this Extension means you are also subject to Google's terms and privacy policies for the data (your text) that you send for
  processing.
            </p>
            <p className="text-slate-300">
                You can review Google's Privacy Policy here: <a href="https://policies.google.com/privacy"
  target="_blank"  rel="noopener noreferrer" className="text-blue-400 hover:underline">https://policies.google.com/privacy</a>.
            </p>
          </section>

          {/* Data Storage */}
          <section className="bg-slate-900/50 backdrop-blur-xl p-8 rounded-xl border border-slate-800">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
              <Server className="w-6 h-6 text-blue-400" />
              Data Security
            </h2>
            <p className="text-slate-300 mb-4">
              Security is handled by leveraging built-in browser features. Your API key is protected by the Chrome browser's secure
  storage system. The Extension itself does not implement any additional data security measures because it does not store your data
   on any server controlled by the developer.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="bg-slate-900/50 backdrop-blur-xl p-8 rounded-xl border border-slate-800">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-400" />
              Children's Privacy
            </h2>
            <p className="text-slate-300 mb-4">
              This Extension is intended for a general audience and is not directed at children under the age of 13. We do not
  knowingly collect any personally identifiable information.
            </p>
          </section>

          {/* Changes to This Privacy Policy */}
          <section className="bg-slate-900/50 backdrop-blur-xl p-8 rounded-xl border border-slate-800">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="w-6 h-6 text-blue-400" />
              Changes to This Privacy Policy
            </h2>
            <p className="text-slate-300 mb-4">
              We may update this Privacy Policy to reflect changes in functionality or legal requirements. Any changes will be posted
  on this page, and you are advised to review it periodically.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-slate-900/50 backdrop-blur-xl p-8 rounded-xl border border-slate-800">
            <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-blue-400" />
              Contact Us
            </h2>
            <p className="text-slate-300 mb-4">
              If you have any questions about this Privacy Policy, please contact us at via Chrome Web Store page.<br />
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default GrammarFixPrivacyPolicy;
